// Mudança de mesa (dono, 02/10/2026).
//
// O celular guarda UMA sessão por loja. Quando o cliente lê o QR de outra
// mesa, ou quando a mesa dele muda (quem abriu mudou, ou o garçom transferiu),
// é aqui que se descobre onde a sessão está e o que oferecer:
//   - quem abriu a mesa, numa mesa vazia: "mudar para cá?" (vai a conta toda);
//   - convidado numa mesa vazia: só o aviso e o caminho de volta;
//   - a mesa dele mudou: o celular segue para a mesa nova sem pedir o CPF;
//   - ele abriu o cardápio ONLINE estando numa mesa aberta: volta para a mesa
//     (useVoltarParaMesa, no fim do arquivo).
import { useCallback, useEffect, useRef, useState } from "react";

import * as api from "./api";
import { carregarDeOutraMesa, limpar, salvar } from "./sessaoMesa";

const AVISO = "mesa_qr_aviso_mudanca";

// O link da mesa só precisa do código: o trecho antes dele é enfeite.
const irParaMesa = (token, { substituir = false } = {}) => {
  const destino = `#/mesa/${token}`;
  if (substituir) window.location.replace(destino);
  else window.location.hash = destino;
};

// O aviso atravessa a troca de endereço (o cardápio é remontado na mesa nova).
const guardarAviso = (token, texto) => {
  try {
    window.sessionStorage.setItem(AVISO, JSON.stringify({ token, texto }));
  } catch {
    /* sem storage: muda de mesa sem o aviso */
  }
};

const lerAviso = (token) => {
  try {
    const dados = JSON.parse(window.sessionStorage.getItem(AVISO) || "null");
    if (!dados || dados.token !== token) return null;
    window.sessionStorage.removeItem(AVISO);
    return dados.texto || null;
  } catch {
    return null;
  }
};

const quemDe = (s) => ({ opened_at: s.opened_at, cliente_id: s.cliente?.id, chave: s.chave });

// Resposta do servidor (minha-mesa, mudar ou o EM_OUTRA_MESA do CPF) no
// formato da sessão que o celular guarda.
const sessaoDe = (d, token) => ({
  token,
  opened_at: d.opened_at,
  mesa: d.mesa,
  cliente: d.cliente,
  chave: d.chave,
  eu: { status: d.eu?.status, papel: d.eu?.papel, codigo_saida: d.eu?.codigo_saida },
});

const textoDoAviso = (d) => `${d.por || "A mesa"} mudou ${d.por ? "a mesa " : ""}para a ${d.mesa?.nome || "mesa nova"}.`;

export function useOutraMesa(token) {
  const [minhaMesa, setMinhaMesa] = useState(null);
  const [mudando, setMudando] = useState(false);
  const [aviso, setAviso] = useState(() => (token ? lerAviso(token) : null));
  const seguindo = useRef(false);

  // Ao abrir o link de uma mesa: a sessão guardada é de outra? Devolve
  // { sessao } quando a mesa do cliente mudou para ESTA (é só entrar), ou
  // { outra: true } quando ele continua com mesa em outro lugar.
  const conferir = useCallback(async () => {
    const guardada = carregarDeOutraMesa(token);
    if (!guardada) return null;
    const r = await api.minhaMesa(guardada.token, quemDe(guardada));
    if (!r.ok) return null; // a mesa dele fechou: vida normal nesta
    const d = r.dados;
    if (d.mesa?.token === token) {
      if (d.mudou) setAviso(textoDoAviso(d));
      return { sessao: sessaoDe(d, token) };
    }
    if (d.eu?.status !== "aprovado" || d.eu?.saiu) return null; // esperando, recusado ou já saiu não prende
    setMinhaMesa(d);
    return { outra: true };
  }, [token]);

  // O CPF digitado aqui é de quem já está em outra mesa, num celular sem a
  // sessão guardada (outro navegador, por exemplo). A resposta só diz em que
  // mesa ele está — sem código nem chave: este aparelho avisa, não muda.
  const definir = useCallback((dados) => setMinhaMesa(dados), []);

  const mudar = useCallback(async () => {
    if (!minhaMesa?.chave || !minhaMesa.mesa?.token || mudando) return { ok: false };
    setMudando(true);
    const r = await api.mudarDeMesa(token, quemDe(minhaMesa), minhaMesa.mesa.token);
    setMudando(false);
    if (!r.ok) return r;
    setMinhaMesa(null);
    return { ok: true, dados: r.dados, sessao: sessaoDe({ ...r.dados, cliente: r.dados.cliente || minhaMesa.cliente }, token) };
  }, [token, minhaMesa, mudando]);

  const voltar = useCallback(() => {
    if (!minhaMesa?.mesa?.token) return;
    // Grava a sessão da mesa dele antes de ir: lá ele entra direto, sem CPF
    salvar(minhaMesa.mesa.token, sessaoDe(minhaMesa, minhaMesa.mesa.token));
    irParaMesa(minhaMesa.mesa.token);
  }, [minhaMesa]);

  // A sessão deste celular não vale mais nesta mesa: antes de dar o cliente
  // por expulso, pergunta se a mesa mudou. Devolve "indo" quando o celular
  // está indo atrás dela, "aqui" quando ela continua nesta mesa (alarme
  // falso) e null quando acabou de verdade.
  const seguir = useCallback(async (quem) => {
    if (!quem?.cliente_id || !quem.chave) return null;
    if (seguindo.current) return "indo";
    seguindo.current = true;
    try {
      const r = await api.minhaMesa(token, quem);
      if (!r.ok) return null;
      const d = r.dados;
      if (!d.mudou || !d.mesa?.token || d.mesa.token === token) return "aqui";
      salvar(d.mesa.token, sessaoDe(d, d.mesa.token));
      guardarAviso(d.mesa.token, textoDoAviso(d));
      irParaMesa(d.mesa.token, { substituir: true });
      return "indo";
    } finally {
      seguindo.current = false;
    }
  }, [token]);

  return { minhaMesa, mudando, aviso, fecharAviso: () => setAviso(null), conferir, definir, mudar, voltar, seguir };
}

// Mesma folga do servidor (`mudanca.HORAS_DE_MESA_VIVA`): mesa aberta há mais
// tempo que isso foi esquecida aberta pela loja e não prende ninguém.
const HORAS_DE_MESA_VIVA = 12;
const ENTRE_CONFERENCIAS_MS = 15000;
// Respostas do `minha-mesa` que dizem que a sessão guardada acabou de verdade.
const SESSAO_ACABOU = ["SESSAO_EXPIRADA", "PARTICIPANTE_INVALIDO"];

const mesaEsquecida = (openedAt) => {
  // Só os milésimos: o servidor grava microssegundos e nem todo navegador lê
  const aberta = Date.parse(String(openedAt || "").replace(/(\.\d{3})\d+/, "$1"));
  return !Number.isFinite(aberta) || Date.now() - aberta > HORAS_DE_MESA_VIVA * 60 * 60 * 1000;
};

// Cardápio ONLINE aberto num celular que está numa mesa aberta (achado 402,
// dono 03/10/2026): leva de volta para a mesa.
//
// A sessão da mesa fica guardada no celular, mas só a rota da mesa olhava para
// ela. Quem caía no cardápio online — pela aba que ficou do antigo botão VER O
// CARDÁPIO, por um link sem a mesa — montava pedido de retirada sentado no
// salão (Mestre dos Jogos: o Erik, com a mesa 5 aberta, voltou para a aba
// antiga e ficou nela). Quem decide é o servidor: só volta quem continua
// liberado numa mesa aberta e ainda não recebeu a saída.
export function useVoltarParaMesa({ ativo }) {
  useEffect(() => {
    if (!ativo) return undefined;
    let vivo = true;
    let conferindo = false;
    let ultima = 0;

    const conferir = async () => {
      if (!vivo || conferindo || document.hidden || Date.now() - ultima < ENTRE_CONFERENCIAS_MS) return;
      const guardada = carregarDeOutraMesa(null); // aqui não existe "esta mesa": vale a de qualquer uma
      if (!guardada || mesaEsquecida(guardada.opened_at)) return;
      conferindo = true;
      ultima = Date.now();
      const r = await api.minhaMesa(guardada.token, quemDe(guardada));
      conferindo = false;
      if (!vivo) return;
      if (!r.ok) {
        // Mesa fechada ou chave que não vale mais: esquece, senão o cardápio
        // online perguntaria a cada visita. Sem rede, tenta na próxima.
        if (SESSAO_ACABOU.includes(r.erro?.codigo)) limpar();
        return;
      }
      const d = r.dados;
      if (d.eu?.status !== "aprovado" || d.eu?.saiu || !d.mesa?.token) return;
      salvar(d.mesa.token, sessaoDe(d, d.mesa.token));
      irParaMesa(d.mesa.token, { substituir: true });
    };
    // A aba antiga quase nunca recarrega: a pessoa só volta para ela
    const aoVoltar = () => { if (!document.hidden) conferir(); };

    conferir();
    document.addEventListener("visibilitychange", aoVoltar);
    return () => {
      vivo = false;
      document.removeEventListener("visibilitychange", aoVoltar);
    };
  }, [ativo]);
}
